import React, { useEffect, useState } from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import atoms from 'ctsutheme/components/atoms';
import { bindLogger } from 'helpers/BindLogger';
import { withAppContext } from '@ctsu/loader';
import {
  setStatusChange,
  cancelRequest,
  getCirb
} from 'actions/protocols/protocoldetail';
import { GetFromCtxValue } from 'helpers';
import { openSnackbar } from 'helpers/Notifier';
import { httpStatusEnum } from 'appconstants';
import { CanUserEditStatusChange } from 'appconstants/Permission';
import PropTypes from 'prop-types';
import { FasRedoAlt } from 'assets/icons';
import { RenderEditable, RenderElement } from 'components/RenderHelpers';
import ViewStatusChange from './ViewStatusChange';
import { EditStatusChange } from './EditStatusChange';

const { Typography, IconButton, Tooltip } = atoms;

const StatusInfo = props => {
  const [disableStatus, setDisableStatus] = useState(null);
  const {
    prtInfo,
    state,
    setState,
    save,
    setSave,
    context,
    onSaveStatusChange,
    onCancelStatusChange,
    refreshSummary,
    closePrtStatusChange,
    multiPhase,
    getCirbRequest
  } = props;

  useEffect(() => {
    setState(false);
  }, [prtInfo, setState]);

  const {
    isRssStatusSync,
    isPIO,
    statusChangeInfo,
    setupIndicator,
    grpProtStatusDate,
    grpProtStatus,
    isRequestCancelable,
    completionDates,
    isIrbExempt,
    nihAdminCode,
    protocolNumber,
    isCirbRequired
  } = prtInfo || {};

  // create a useEffect and dispatch action to fetch details from cirb screen.
  useEffect(() => {
    if(grpProtStatus === 'Complete' && isCirbRequired && nihAdminCode === 'CTEP' && protocolNumber) {
       bindLogger(getCirbRequest, context)(protocolNumber).subscribe(resp => {
        setDisableStatus(resp?.response?.data.some((item) => item.approvalType === "CLOSURE"))
      }
     );   
    }
  }, [protocolNumber])

  return (
    <RenderEditable
      state={state}
      editable={CanUserEditStatusChange()}
      setState={setState}
      title="Status Change Information"
      subHeaderItems={(
        <Tooltip
          tooltipplacementbottom="true"
          title="Refresh Status Change Information"
          aria-label="Refresh Status Change Information"
          style={{ display: 'inline-flex' }}
        >
          <Typography component="span" inline link icon>
            <IconButton
              aria-label="Refresh Summary"
              data-id="refreshSummary"
              color="inherit"
              onClick={refreshSummary}
            >
              <FasRedoAlt />
            </IconButton>
          </Typography>
        </Tooltip>
      )}
    >
      <RenderElement access={(isPIO === true ? isRssStatusSync === true : true)  && state === false}>
        <ViewStatusChange
          statusChangeInfo={statusChangeInfo || {}}
        />
      </RenderElement>
      <RenderElement access={(isPIO === true ? isRssStatusSync === true : true) && state === true}>
        <EditStatusChange
          statusChangeInfo={statusChangeInfo}
          setupIndicator={setupIndicator}
          grpProtStatusDate={grpProtStatusDate}
          grpProtStatus={grpProtStatus}
          completionDates={completionDates}
          isRequestCancelable={isRequestCancelable}
          closeStatusChange={closePrtStatusChange}
          onSaveStatusChange={onSaveStatusChange}
          onCancelStatusChange={onCancelStatusChange}
          save={save}
          setSave={() => setSave(!save)}
          multiPhase={multiPhase}
          isIrbExempt={isIrbExempt}
          disableStatus={disableStatus}
          isCirbRequired={isCirbRequired}
          nihAdminCode={nihAdminCode}
        />
      </RenderElement>
      <RenderElement access={isPIO === true && (isRssStatusSync === false || isRssStatusSync === null)}>
        <Typography data-id="prtStatusSyncMsg" style={{ padding: '0 1rem 1rem', textAlign: 'left'}}>
          {GetFromCtxValue(context, 'PROTOCOL_STATUS_SYNC')}
        </Typography>
      </RenderElement>
    </RenderEditable>
  );
};

StatusInfo.propTypes = {
  prtInfo: PropTypes.oneOfType([PropTypes.object]),
  onSaveStatusChange: PropTypes.func,
  setState: PropTypes.func,
  save: PropTypes.bool,
  setSave: PropTypes.func,
  onCancelStatusChange: PropTypes.func,
  refreshSummary: PropTypes.func,
  context: PropTypes.oneOfType([PropTypes.object]),
  closePrtStatusChange: PropTypes.func
};

StatusInfo.defaultProps = {
  prtInfo: null,
  onSaveStatusChange: null,
  setState: null,
  save: false,
  setSave: null,
  onCancelStatusChange: null,
  refreshSummary: null,
  context: null,
  closePrtStatusChange: null
};

const withConnectState = connect(
  (state, ownProps) => ({
    openSetupInfo: state.openData.openData
  }),
  dispatch => bindActionCreators(
    {
      onSetStatusChange: setStatusChange,
      onCancelRequest: cancelRequest,
      getCirbRequest: getCirb
    },
    dispatch
  )
);

const StatusChange = compose(
  withConnectState,
  withAppContext,
  withState('state', 'setState', false),
  withState('save', 'setSave', false),
  withHandlers({
    closePrtStatusChange: props => event => {
      const { setState, state } = props;
      setState(!state);
    }
  }),
  withHandlers({
    onSaveStatusChange: props => status => {
      const {
        context,
        prtInfo,
        setSave,
        closePrtStatusChange,
        onSetStatusChange
      } = props;
      bindLogger(onSetStatusChange, context)(status, prtInfo.prtId).subscribe(
        resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'PROTOCOL_STATUS_CHANGE_SUCCESS'),
              variant: 'success',
              name: 'status_change'
            });
            setSave(false);
            closePrtStatusChange();
          }
        },
        err => {
          setSave(false);
        }
      );
    },
    onCancelStatusChange: props => status => {
      const {
        context,
        closePrtStatusChange,
        onCancelRequest,
        prtInfo
      } = props;
      bindLogger(onCancelRequest, context)(prtInfo.prtId).subscribe(
        resp => {
          if (resp.status === httpStatusEnum.SUCCESS) {
            openSnackbar({
              message: GetFromCtxValue(context, 'PROTOCOL_STATUS_CHANGE_CANCEL'),
              variant: 'success',
              name: 'status_change_cancel'
            });
            closePrtStatusChange();
          }
        }
      );
    }
  })
)(StatusInfo);

export default StatusChange;
